/**
 * GestorCapas — gestor de capas (polígonos/líneas/puntos) del panel aosenuma.
 * Reemplaza la antigua "Carga masiva". Un solo gestor con sub-tabs:
 *   - Subir:      wizard (Operativa = predios reales | Informativa = referencia)
 *   - Predios:    registro + CRUD de capas operativas (lote-delete / deshacer)
 *   - Referencia: registro + CRUD de capas informativas (editar / borrar)
 *
 * Acceso: SOLO admin_aosenuma (super-admins incluidos). Los técnicos NO — la
 * carga masiva los confunde; usan la captura individual. Gate en 3 capas: nav
 * del shell + este check + requireRole('admin_aosenuma') en el BFF. Las RPC son
 * service_role-only.
 */
import { useState } from 'react';
import { useSession } from '@aosenuma/lib-supabase';
import { SubirOperativa } from './capas/SubirOperativa';
import { SubirInformativa } from './capas/SubirInformativa';
import { RegistroCapas } from './capas/RegistroCapas';

type Tab = 'subir' | 'predios' | 'referencia';
type SubirTipo = 'operativa' | 'informativa';

const TABS: { key: Tab; label: string }[] = [
  { key: 'subir', label: 'Subir' },
  { key: 'predios', label: 'Predios (operativas)' },
  { key: 'referencia', label: 'Referencia (informativas)' },
];

export function GestorCapas() {
  const { rolApp } = useSession();
  const isAdmin = rolApp === 'admin_aosenuma'; // incluye super-admins
  const [tab, setTab] = useState<Tab>('subir');
  const [subirTipo, setSubirTipo] = useState<SubirTipo>('operativa');

  if (!isAdmin) {
    return (
      <section className="panel">
        <header className="panel-header">
          <h1>Sin permisos</h1>
          <p className="panel-sub">Esta sección es solo para administradores de aosenuma.</p>
        </header>
      </section>
    );
  }

  return (
    <section className="panel">
      <header className="panel-header">
        <h1>Gestor de capas</h1>
        <p className="panel-sub">
          Sube y administra capas: <strong>operativas</strong> (predios reales con semáforo/gestión) o{' '}
          <strong>informativas</strong> (referencia: líneas de transmisión, ductos, agua…). KML, KMZ o GeoJSON.
        </p>
      </header>

      {/* Sub-tabs */}
      <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border, rgba(255,255,255,0.12))', marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--accent, #4A9CA5)' : '2px solid transparent',
              background: 'none',
              color: tab === t.key ? 'var(--text, inherit)' : 'var(--text-dim, #9aa)',
              fontWeight: tab === t.key ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'subir' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => setSubirTipo('operativa')}
              aria-pressed={subirTipo === 'operativa'}
              style={segStyle(subirTipo === 'operativa')}
            >
              Operativa (predios)
            </button>
            <button
              type="button"
              onClick={() => setSubirTipo('informativa')}
              aria-pressed={subirTipo === 'informativa'}
              style={segStyle(subirTipo === 'informativa')}
            >
              Informativa (referencia)
            </button>
          </div>
          {subirTipo === 'operativa'
            ? <SubirOperativa onCreated={() => setTab('predios')} />
            : <SubirInformativa onCreated={() => setTab('referencia')} />}
        </div>
      )}

      {tab === 'predios' && <RegistroCapas tipo="operativa" />}
      {tab === 'referencia' && <RegistroCapas tipo="informativa" />}
    </section>
  );
}

function segStyle(active: boolean): React.CSSProperties {
  return {
    padding: '6px 14px',
    borderRadius: 'var(--radius-md, 8px)',
    border: active ? '1px solid var(--accent, #4A9CA5)' : '1px solid var(--border, rgba(255,255,255,0.18))',
    background: active ? 'rgba(74, 156, 165, 0.16)' : 'transparent',
    color: 'inherit',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
  };
}
